/**
 * Palette section helpers (PR Q5 · §Q5.2).
 *
 * Build the empty-query sections (Recents · Starred · Trending) and
 * partition active-query hits into kind-buckets so the SearchPalette
 * can render them under labels without inline filtering.
 *
 * `scopeCommands` narrows entity commands to those that stay within
 * the focused domain — global tools (group: 'tools') drop out.
 */
import type { SearchHit, SearchRecordKind } from '../search/searchRecords.js';
import type { EntityCommand } from './entityCommands.js';
import type { TrailEntry } from './workflowTrail.js';

export const RECENTS_CAP = 5;
export const STARRED_CAP = 9;
export const TRENDING_CAP = 5;

export interface PaletteItem {
  /** Entity id or command id. */
  key: string;
  label: string;
  /** Short outcome line (e.g. "opened 2m ago", "+47 views"). */
  outcome?: string;
  /** Single-letter shortcut, if any (currently used by command rows). */
  shortcut?: string;
}

export interface PaletteSection {
  label: string;
  items: PaletteItem[];
}

export interface TrendingEntity {
  entityId: string;
  /** Week-over-week delta (e.g. 47 → "+47 views vs last week"). */
  deltaWeek: number;
}

/**
 * Build the empty-query sections in fixed order: Recents · Starred ·
 * Trending. Sections with no data are omitted.
 */
export function paletteEmptyState(
  trail: readonly TrailEntry[],
  pinned: readonly string[],
  trends?: readonly TrendingEntity[],
): PaletteSection[] {
  const out: PaletteSection[] = [];
  if (trail.length > 0) {
    out.push({
      label: 'RECENT',
      items: trail.slice(0, RECENTS_CAP).map((entry) => ({
        key: `${entry.kind}:${entry.id}`, label: entry.id, outcome: 'opened recently',
      })),
    });
  }
  if (pinned.length > 0) {
    out.push({
      label: 'STARRED',
      items: pinned.slice(0, STARRED_CAP).map((id, i) => ({
        key: id, label: id, outcome: 'pinned entity', shortcut: `⌘${i + 1}`,
      })),
    });
  }
  if (trends && trends.length > 0) {
    out.push({
      label: 'TRENDING THIS WEEK',
      items: trends.slice(0, TRENDING_CAP).map((t) => ({
        key: t.entityId, label: t.entityId,
        outcome: `+${t.deltaWeek} views vs last week`,
      })),
    });
  }
  return out;
}

/** Fixed display order for grouped search-result sections. */
export const SEARCH_KIND_ORDER: readonly SearchRecordKind[] = [
  'entity',
  'field',
  'firestore-path',
  'surface',
  'cloud-function',
  'event-type',
  'hub',
  'feature',
  'flag',
  'statemachine',
];

/** Plural section-header text for each kind, e.g. "ENTITIES", "CLOUD FUNCTIONS". */
export const SEARCH_KIND_LABELS: Record<SearchRecordKind, string> = {
  entity: 'ENTITIES',
  field: 'FIELDS',
  'firestore-path': 'FIRESTORE PATHS',
  surface: 'SURFACES',
  'cloud-function': 'CLOUD FUNCTIONS',
  'event-type': 'EVENT TYPES',
  hub: 'HUBS',
  feature: 'FEATURES',
  flag: 'FLAGS',
  statemachine: 'STATE MACHINES',
};

export type GroupedHits = Record<SearchRecordKind, SearchHit[]>;

export function groupResults(hits: readonly SearchHit[]): GroupedHits {
  const grouped = Object.fromEntries(
    SEARCH_KIND_ORDER.map((kind) => [kind, [] as SearchHit[]]),
  ) as GroupedHits;
  for (const h of hits) {
    grouped[h.record.kind].push(h);
  }
  return grouped;
}

/**
 * When a focused domain is supplied, drop commands whose `group` is
 * `'tools'` — those are global lenses (Persona Slicer, Storyboard,
 * etc.) and shouldn't crowd the domain-scoped section.
 */
export function scopeCommands(
  commands: readonly EntityCommand[],
  focusedDomain?: string,
): EntityCommand[] {
  if (!focusedDomain) return [...commands];
  return commands.filter((c) => c.group !== 'tools');
}
